/**
 * Constants for the Vendor Detail Page
 */

// ============================================================================
// Query Configuration
// ============================================================================

export const VENDOR_DETAIL_QUERY_CONFIG = {
  staleTime: 30 * 1000, // 30 seconds
  gcTime: 5 * 60 * 1000, // 5 minutes
  retry: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

// ============================================================================
// Company Info Fields Configuration
// ============================================================================

export const COMPANY_INFO_FIELDS = [
  { key: 'company_name', label: 'Company Name', icon: 'Building2' },
  { key: 'founded', label: 'Founded', icon: 'Calendar' },
  { key: 'headquarters', label: 'Headquarters', icon: 'MapPin' },
  { key: 'no_of_employees', label: 'Employees', icon: 'Users' },
  { key: 'ownership', label: 'Ownership', icon: 'Landmark' },
  { key: 'primary_domain', label: 'Primary Domain', icon: 'Globe' },
] as const;

export type CompanyInfoFieldKey = (typeof COMPANY_INFO_FIELDS)[number]['key'];
export type CompanyInfoField = (typeof COMPANY_INFO_FIELDS)[number];

// ============================================================================
// Intelligence Items Configuration
// ============================================================================

export const INTELLIGENCE_ITEMS = [
  { key: 'breach_history', label: 'Breach History', countKey: 'total_breaches', icon: 'ShieldAlert' },
  { key: 'dark_web', label: 'Dark Web Chatter', countKey: 'total_dark_web_chatter_findings', icon: 'Eye' },
  { key: 'compliance', label: 'Compliance', countKey: 'total_compliances', icon: 'CheckCircle' },
  { key: 'news', label: 'News', countKey: 'total_news_articles', icon: 'Newspaper' },
] as const;

export type IntelligenceItemKey = (typeof INTELLIGENCE_ITEMS)[number]['key'];
export type IntelligenceCountKey = (typeof INTELLIGENCE_ITEMS)[number]['countKey'];
export type IntelligenceItem = (typeof INTELLIGENCE_ITEMS)[number];

// ============================================================================
// Assessment Step Names
// ============================================================================

export const ASSESSMENT_STEP_NAMES: Record<number, string> = {
  1: 'Intake Form',
  2: 'Information Gathering',
  3: 'Generating Results',
  4: 'Review Results',
  5: 'Completed',
};

// ============================================================================
// Status Labels Mapping
// ============================================================================

export const STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress',
  reassessment_in_progress: 'In Progress',
  input_needed: 'In Progress',
  form_sent: 'In Progress',
  complete: 'Complete',
  needs_review: 'Needs Review',
};

export type VendorStatus = keyof typeof STATUS_LABELS;

// ============================================================================
// Risk Level Styles
// ============================================================================

export const RISK_LEVEL_STYLES = {
  High: {
    bg: 'bg-red-50',
    text: 'text-red-600',
    border: 'border-red-200',
  },
  Medium: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-600',
    border: 'border-yellow-200',
  },
  Low: {
    bg: 'bg-green-50',
    text: 'text-green-600',
    border: 'border-green-200',
  },
} as const;

export type RiskLevel = keyof typeof RISK_LEVEL_STYLES;
export type RiskLevelStyle = (typeof RISK_LEVEL_STYLES)[RiskLevel];

// ============================================================================
// Default Values
// ============================================================================

export const DEFAULT_TOTAL_STEPS = 5;
export const LOADING_SLOW_THRESHOLD_MS = 3000;
export const REQUEST_TIMEOUT_MS = 30000;
